import React from "react";
import { bind } from "decko";

import Config from "./include/Config";
import Util from "./include/Util";
import DialogOverlay from "./DialogOverlay";
import GridOptionSelector from "./GridOptionSelector";
import EventTitleField from "./EventTitleField";
import L1PlayerSelector from "./L1PlayerSelector";
import KeeperSelector from "./KeeperSelector";

/**
 * This component is used for providing a list of options for exporting an event
 *
 * Expected props:
 *      onSave: function (tag, score, description) - callback when event should be exported
 *      onCancel: function () - callback on exit w/o exporting
 *      game: { ... }
 *      selectedTeam: Object - should contain "name" property
 *      options: array of possible selections, in this format:
 *          {
 *              key: string - something unique
 *              tag: {action: "something", team: {type: "team", value: "something"}} - tag of output event
 *              description: { default text per localization, omittable if tag has a default text
 *                  no: "something norwegian",
 *                  se: "something swedish"
 *              }
 *          }
 */
export default class MultipleEventDialog extends React.Component {
    constructor (props) {
        super(props);

        this.state = this.getDefaultState(props);
        if (this.props.selectedTeam.id === this.props.homeTeam.id) {
            this.state.keeper = this.props.visitingTeamKeeper;
        } else {
            this.state.keeper = this.props.homeTeamKeeper;
        }
    }

    getDefaultState (props) {
        return {
            key: props.options[0].key,
            description: null,
            player: null,
            keeperSelectorVisible: false,
        };
    }

    getOption (key) {
        return this.props.options.filter((o) => o.key === key)[0];
    }

    componentWillReceiveProps(nextProps) {
        let a = this.props.options.map((o) => o.key).join(",");
        let b = nextProps.options.map((o) => o.key).join(",");
        if (a !== b) {
            this.setState(this.getDefaultState(nextProps));
        } else if (this.props.selectedTeam.id !== nextProps.selectedTeam.id) {
            this.setState({player: null});
        }
    }

    getDescription (key) {
        if (this.state.description !== null) {
            //Manual override
            return this.state.description;
        }

        if (key === undefined) key = this.state.key;

        //Check for a freetext override
        let option = this.getOption(key);
        if (option.description !== undefined) {
            return option.description[Config.language];
        }

        //Use the default text from a tag
        let tag = this.getTag();
        return Util.createAutomaticTitle(tag, this.props.game);
    }

    getTag () {
        // Very important to make a deep copy here
        let tag = JSON.parse(JSON.stringify(this.getOption(this.state.key).tag));

        if (this.state.player !== null) {
            tag.player = {
                type: "player",
                id: this.state.player.id,
                value: this.state.player.value
            }
        }
        if (tag.action === "shot" && this.state.keeper !== null && this.state.keeper !== undefined && this.state.keeper.id) {
            tag.keeper = {
                type: "player",
                id: this.state.keeper.id,
                value: this.state.keeper.value
            }
        }
        return tag;
    }

    @bind
    changeOption (key) {
        this.setState({
            key: key,
            description: null
        });
    }

    @bind
    selectPlayer (player) {
        this.setState({player: player});
    }

    @bind
    selectKeeper(keeper) {
        this.setState({keeper: keeper});
    }

    @bind
    save () {
        let tag = this.getTag();
        this.props.onSave(tag, undefined, this.getDescription());
    }

    render () {

        let optionKeys = this.props.options.map((o) => o.key);
        let columns = 4;
        if (Math.ceil(optionKeys.length / 4) === Math.ceil(optionKeys.length / 3)) {
            columns = 3;
        }
        const option = this.getOption(this.state.key);

        const tagAction = option.tag.action;
        let shirtSelector = null;
        if (tagAction === "yellow card" || tagAction === "red card" || tagAction === "shot") {
            shirtSelector = (
                <div className="col-xs-12 text-center" style={{marginTop: "10px"}}>
                    <L1PlayerSelector
                        player={this.state.player}
                        lineup={this.props.lineup}
                        playersOnPitch={this.props.playersOnPitch}
                        playersUnavailable={this.props.playersUnavailable}
                        selectedTeam={this.props.selectedTeam}
                        onChange={this.selectPlayer}
                    />
                </div>
            );
        }

        let keeperSelector = null;
        if (tagAction === "shot") {
            keeperSelector = (
                <KeeperSelector
                    lineup={this.props.lineup}
                    keeper={this.state.keeper}
                    team={
                        this.props.selectedTeam.id === this.props.homeTeam.id ?
                            this.props.visitingTeam : this.props.homeTeam
                    }
                    onKeeperSelected={this.selectKeeper}
                />
            );
        }

        let textBoxOverride = null;
        if (option.displayTextField) {
            textBoxOverride = (
                <div className="col-xs-12 text-center" style={{marginTop: "10px"}}>
                    <EventTitleField
                        value={this.getDescription(this.state.key)}
                        onChange={(d) => this.setState({description: d})} />
                </div>
            );
        }

        return (
            <DialogOverlay
                hotkeyHandler={this.props.hotkeyHandler}
                onSubmit={this.save}
                onCancel={this.props.onCancel}>
                <div className="row" style={{marginBottom: "16px"}}>
                    <div className="col-xs-12 text-center">
                        {this.getDescription()}
                    </div>
                    <div className="col-xs-12 text-outline" style={{marginTop: "10px"}}>
                        { optionKeys.length > 1 &&
                            <GridOptionSelector
                                hotkeyHandler={this.props.hotkeyHandler}
                                columns={columns}
                                options={optionKeys}
                                selected={this.state.key}
                                onSelectOption={this.changeOption}
                            />
                        }
                    </div>
                    {shirtSelector}
                    {keeperSelector}
                    {textBoxOverride}
                </div>
            </DialogOverlay>
        );
    }
}



// WEBPACK FOOTER //
// ./src/js/MultipleEventDialog.js